import { useCallback, useContext } from 'react'

import { uuid } from '@sanity/uuid'

import { useAuthV2 } from '~/contexts/AuthContext'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { SelectedArtworkContext } from '~/contexts/SelectedArtworkContext'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'

import {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '../utils/posthogEvents'

import useMabPostHog from './useMabPostHog'

const webProduct = PushWebProduct.InstantValuation

const useInstantValuationFormAnalytics = () => {
  const { state, stateDispatch } = useContext(GlobalStoreContext)
  const { currentArtworkState } = useContext(SelectedArtworkContext)
  const { pushEvent } = useMabPostHog()
  const { user } = useAuthV2()

  /**
   * Initial step - form submitted from the Instant Valuation widget.
   */
  const handleIVFormSubmission = useCallback(
    (artistName?: string, artworkName?: string) => {
      const eventName = 'Instant Valuation Requested'

      const interactionType = PushInteractionType.Submission
      const interactionLocation = PushInteractionLocation.InstantValuationWidget

      const metaData = {
        artistName: currentArtworkState.artist.name || artistName,
        artworkName: currentArtworkState.selectedArtwork.name || artworkName,
      }

      stateDispatch({
        type: GlobalStoreProps.UPDATE_VALUATION_FORM_ANALYTICS_STATE,
        payload: {
          artistName,
          artworkName,
        },
      })

      pushEvent(eventName, {
        webProduct,
        interactionType,
        interactionLocation,
        metaData,
      })
    },
    [pushEvent, stateDispatch, currentArtworkState]
  )

  /**
   * Instant Valuation page loaded.
   */
  const handleInstantValuationPageLoaded = useCallback(
    (valueDisplayed: string) => {
      const eventName = 'Instant Valuation Page Loaded'

      const payload = {
        webProduct,
        interactionType: PushInteractionType.PageLoad,
        interactionLocation: PushInteractionLocation.InstantValuationPage,
        metaData: {
          valueDisplayed,
          ...state.valuationFormAnalytics,
        },
      }

      stateDispatch({
        type: GlobalStoreProps.UPDATE_VALUATION_FORM_ANALYTICS_STATE,
        payload: {
          ...state.valuationFormAnalytics,
          valueDisplayed,
        },
      })

      pushEvent(eventName, payload)
    },
    [pushEvent, state.valuationFormAnalytics]
  )

  /**
   * Form initiation button from the Instant Valuation page.
   */
  const handleValuationButtonClick = useCallback(() => {
    const eventName = 'Request Appraisal Clicked'

    // Keep the id the page (or the valuation journey drawer) already set so
    // the whole instant journey shares one formLaunchId
    const formId = state.formAnalytics?.formLaunchId || uuid()
    const formType = FormTypes.InstantValuation
    const valuationType = state.formAnalytics?.valuationType

    stateDispatch({
      type: GlobalStoreProps.UPDATE_FORM_ANALYTICS_STATE,
      payload: {
        webProduct,
        formLaunchId: formId,
        formType,
        formStepNumber: 3, // initiate on slide 3 for instant valuation so when slide loaded it becomes 4.
        interactionLocation: FormTypes.valuation,
        valuationType,
        ...state.valuationFormAnalytics,
      },
    })

    pushEvent(eventName, {
      webProduct,
      formLaunchId: formId,
      valuationType,
      interactionType: PushInteractionType.Click,
      interactionLocation: PushInteractionLocation.InstantValuationPage,
      metaData: {
        ...state.valuationFormAnalytics,
      },
    })
  }, [
    pushEvent,
    stateDispatch,
    state.valuationFormAnalytics,
    state.formAnalytics?.formLaunchId,
    state.formAnalytics?.valuationType,
  ])

  return {
    handleIVFormSubmission,
    handleInstantValuationPageLoaded,
    handleValuationButtonClick,
  }
}

export default useInstantValuationFormAnalytics
